import { DossierDetailsContent, DossierDetailsHeader, DossierDetailsPanel } from "@/app/pages/dossier/dossier-details-panel";
import { BetrokkenStation, useGetDossierBetrokkenStationsById } from "@/entities/betrokken-stations";
import {
    AangevraagdeVestiging,
    BeheerVestiging,
    useGetAangevraagdeVestigingen,
    useGetBeheerVestigingenByStations,
    usePatchDossierVestigingen,
    Vestiging,
} from "@/entities/vestiging";
import { RoleGate, UserRole } from "@/features/auth";
import { useDossierId } from "@/features/dossier-details";
import { ErrorDisplay } from "@/shared/components/error";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useArchiveVisibility } from "@/shared/hooks";
import { useCallback, useMemo, useState } from "react";
import { FaciliteitenDetailsReadOnly } from "../alleen-lezen";
import { FaciliteitenDetailsKlantenservice } from "../klantenservice/shared/faciliteiten-details-klantenservice";
import { FaciliteitenDetailsRegievoerder } from "../regievoerder";

export type FaciliteitenVariant = "nieuweAanvraagRegievoerder" | "bestaandeAanvraagRegievoerder" | "openstaandeAanvraagKlantenservice";

type VestigingUpdates = Record<AangevraagdeVestiging["id"], AangevraagdeVestiging>;
export type VestigingenUpdateHandler = (updates: VestigingUpdates) => Promise<void>;

function mapVestigingen(aangevraagdeVestigingen: AangevraagdeVestiging[], beheerVestigingen: BeheerVestiging[]): Vestiging[] {
    return Vestiging.sort(
        aangevraagdeVestigingen.reduce<Vestiging[]>((acc, aangevraagdeVestiging) => {
            const beheerVestiging = beheerVestigingen.find((bv) => bv.id === aangevraagdeVestiging.vestigingId);

            if (!beheerVestiging) {
                console.error(
                    `Geen beheervestiging gevonden voor aangevraagdeVestiging met id ${aangevraagdeVestiging.id} en vestigingId ${aangevraagdeVestiging.vestigingId}`
                );
                return acc;
            }

            const openingstijden = AangevraagdeVestiging.getOpeningstijden(aangevraagdeVestiging);
            const currentOpeningstijden = openingstijden
                ? {
                      ...openingstijden,
                      openTot:
                          openingstijden.aangepasteOpenTot &&
                          (openingstijden.aangepasteOpenTotStatus === "Actief" || openingstijden.aangepasteOpenTotStatus === "Gestopt")
                              ? openingstijden.aangepasteOpenTot
                              : openingstijden.openTot,
                  }
                : BeheerVestiging.getOpeningstijdenVandaag(beheerVestiging);

            acc.push({
                beheerVestiging,
                aangevraagdeVestiging,
                currentOpeningstijden,
            });
            return acc;
        }, [])
    );
}

function FaciliteitenDetailsSkeleton() {
    return (
        <div className="space-y-6">
            <Skeleton className="h-8 w-32" />
            <div className="space-y-4">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
            </div>
            <div className="flex items-center justify-between">
                <Skeleton className="h-9 w-32" />
                <div className="flex items-center space-x-4">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-9 w-32" />
                </div>
            </div>
        </div>
    );
}

export function FaciliteitenDetails() {
    const id = useDossierId();
    const isArchived = useArchiveVisibility();
    const [updateError, setUpdateError] = useState<Error | null>(null);
    const {
        data: betrokkenStations,
        isPending: betrokkenStationsPending,
        error: betrokkenStationsError,
    } = useGetDossierBetrokkenStationsById(id, {
        select: BetrokkenStation.filterForType("faciliteiten"),
    });
    const {
        data: aangevraagdeVestigingen = [],
        isPending: aangevraagdPending,
        error: aangevraagdError,
    } = useGetAangevraagdeVestigingen(id);
    const betrokkenStationsCodes = useMemo(() => betrokkenStations?.map((bs) => bs.stationsCode.toLowerCase()) || [], [betrokkenStations]);
    const {
        data: beheerVestigingen = [],
        isPending: vestigingenPending,
        error: vestigingenError,
    } = useGetBeheerVestigingenByStations(betrokkenStationsCodes);
    const { mutateAsync: mutateVestigingen } = usePatchDossierVestigingen();

    const vestigingen = useMemo(
        () => mapVestigingen(aangevraagdeVestigingen, beheerVestigingen),
        [aangevraagdeVestigingen, beheerVestigingen]
    );

    const handleVestigingenUpdate: VestigingenUpdateHandler = useCallback(
        async (updates: VestigingUpdates) => {
            setUpdateError(null);
            try {
                const updatedVestigingen = Object.values(updates).map(AangevraagdeVestiging.toDto);

                await mutateVestigingen({
                    dossierId: id,
                    vestigingen: updatedVestigingen,
                });
            } catch (error) {
                setUpdateError(error as Error);
                throw error;
            }
        },
        [id, mutateVestigingen]
    );

    if (aangevraagdPending || vestigingenPending || betrokkenStationsPending) {
        return (
            <DossierDetailsPanel testId="faciliteiten-skeleton">
                <DossierDetailsHeader section="Faciliteiten" showBackButton />
                <DossierDetailsContent>
                    <FaciliteitenDetailsSkeleton />
                </DossierDetailsContent>
            </DossierDetailsPanel>
        );
    }

    const error = betrokkenStationsError || aangevraagdError || vestigingenError;
    if (error) {
        return (
            <DossierDetailsPanel testId="faciliteiten-error">
                <DossierDetailsHeader section="Faciliteiten" showBackButton />
                <DossierDetailsContent>
                    <ErrorDisplay
                        error={error}
                        variant="section"
                        title="Fout bij het laden van faciliteiten"
                        onRetry={() => window.location.reload()}
                    />
                </DossierDetailsContent>
            </DossierDetailsPanel>
        );
    }

    return (
        <>
            {updateError && <ErrorDisplay error={updateError} title="Fout bij het bijwerken van faciliteiten" />}
            {isArchived ? (
                <FaciliteitenDetailsReadOnly vestigingen={vestigingen} />
            ) : (
                <>
                    <RoleGate roles={[UserRole.REGIEVOERDER]}>
                        <FaciliteitenDetailsRegievoerder
                            dossierId={id}
                            vestigingen={vestigingen}
                            beheerVestigingen={beheerVestigingen}
                            onVestigingenUpdate={handleVestigingenUpdate}
                            betrokkenStations={betrokkenStations}
                        />
                    </RoleGate>
                    <RoleGate roles={[UserRole.KLANTENSERVICE]}>
                        <FaciliteitenDetailsKlantenservice vestigingen={vestigingen} onVestigingenUpdate={handleVestigingenUpdate} />
                    </RoleGate>
                    <RoleGate roles={[UserRole.SUPPORTCONSULTANT, UserRole.ALLEENLEZEN]}>
                        <FaciliteitenDetailsReadOnly vestigingen={vestigingen} />
                    </RoleGate>
                </>
            )}
        </>
    );
}
